import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import TeacherNavbar from '../components/TeacherNavbar';
import {
  Box,
  Avatar,
  Chip,
  Dialog as MuiDialog,
  List,
  Stack,
  ListItemButton,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import Slide from '@mui/material/Slide';
import VerifiedIcon from '@mui/icons-material/Verified';
import MicIcon from '@mui/icons-material/Mic';
import VoiceCommentRecorder from '../components/VoiceMessage';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  InputBase, 
  Paper, 
} from '@mui/material';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import SendIcon from '@mui/icons-material/Send';
import { getAuth } from 'firebase/auth';
import { signOut } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  where,
  query,
  deleteDoc,
  Timestamp,
  orderBy, 
  limit, 
  startAfter, 
  writeBatch
} from 'firebase/firestore';
import { ref, deleteObject, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import WhatshotIcon from '@mui/icons-material/Whatshot'; 
import CheckIcon from '@mui/icons-material/Check';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { useTranslation } from 'react-i18next';

const BottomTransition = React.forwardRef(function BottomTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function TeacherForum() {

  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const scrollToPostId = searchParams.get('postId');

  const [posts, setPosts] = useState([]);
  const [postFilter, setPostFilter] = useState('all');
  const [lastVisible, setLastVisible] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedPost, setSelectedPost] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [newCommentContent, setNewCommentContent] = useState('');
  const [newCommentImages, setNewCommentImages] = useState([]);
  const { groupId } = useParams();
  const auth = getAuth();
  const loadMoreRef = useRef(null);
  const [carouselIndex, setCarouselIndex] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isCommentsOverlayOpen, setIsCommentsOverlayOpen] = useState(false);
  const hasCommentPermission = () => true;
  const hasDeletePostPermission = () => true;
  const hasDeleteCommentPermission = () => true;

  const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    if (!parts.length) return t('?');
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || t('?');
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  };
  const formatDate = (ts) =>
    ts?.toDate ? ts.toDate().toLocaleString() : '';

  // 🔹 Convert Firestore Timestamp to millis
  const tsToMillis = (ts) =>
    ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : 0;

  // 🔹 Check if a timestamp is "today"
  const isToday = (ts) => {
    if (!ts) return false;
    const d = new Date(tsToMillis(ts));
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  };
  const handleAddImages = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setNewImages((prev) => [...prev, ...files]);
    setPreviewImages((prev) => [...prev, ...files.map((file) => URL.createObjectURL(file))]);
  };
  const handleRemovePreview = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };
  const handleSlideChange = (postId, direction, length) => {
    if (!length) return;
    setCarouselIndex((prev) => {
      const current = prev[postId] ?? 0;
      const next =
        direction === 'next'
          ? (current + 1) % length
          : (current - 1 + length) % length;
      return { ...prev, [postId]: next };
    });
  };
  
  // Fetch a small preview of comments so they stay visible under each post even après refresh
  const attachCommentsPreview = useCallback(async (postsToAugment) => {
    if (!postsToAugment.length) return postsToAugment;
    try {
      const augmented = await Promise.all(
        postsToAugment.map(async (post) => {
          const commentsRef = collection(
            doc(db, 'groups', groupId),
            'forum',
            post.id,
            'comments'
          );

          // Get ALL comments for accurate count + latest 3 as preview
          const commentsSnapshot = await getDocs(
            query(commentsRef, orderBy('createdAt', 'desc'))
          );

          const allComments = commentsSnapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          const previewComments = allComments.slice(0, 3);

          return {
            ...post,
            comments: previewComments,            // used as preview under the post
            commentsCount: allComments.length || 0, // used for "most engaging"
          };
        })
      );
      return augmented;
    } catch (err) {
      console.error('Erreur lors de la récupération des commentaires :', err);
      return postsToAugment;
    }
  }, [groupId]);
  

  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);

  /*const openCommentsOverlay = async (post) => {
    setSelectedPost(post); // Sélectionner le post
    try {
      await fetchComments(post.id); // Appeler fetchComments pour le post
      setIsCommentsOverlayOpen(true); // Ouvrir le Drawer
    } catch (error) {
      console.error("Erreur lors du chargement des commentaires :", error);
    }
  };*/
  
  const handleAudioRecorded = (audioBlob) => {
     // Convertir le blob en File, pour avoir un type plus précis et un nom
     const audioFile = new File([audioBlob], `voice_${Date.now()}.webm`, {
      type: "audio/webm",
     });
     // L’ajouter à la liste (comme vous le faites pour les images)
     setNewCommentImages((prevImages) => [...prevImages, audioFile]);
   };

  const resetCreatePostFields = () => {
    setNewTitle('');
    setNewContent('');
    setNewImages([]);
    setPreviewImages([]);
  };
  
  
  const closeCommentsOverlay = () => {
    setIsCommentsOverlayOpen(false);
    setShowVoiceRecorder(false);
  };


  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const postId = urlParams.get('postId');
    const scrolledPostId = sessionStorage.getItem('scrolledPostId');
  
    if (postId && postId !== scrolledPostId) {
      const postElement = document.getElementById(`post-${postId}`);
      if (postElement) {
        postElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        sessionStorage.setItem('scrolledPostId', postId);
      }
    }
  }, [posts]); 
  

  
  //If current User is not TEACHER => SIGN THE OUT !!
  useEffect(() => {
    const verifyTeacher = async () => {
      try {
        const user = auth.currentUser;
      
        // If no user is signed in, do nothing
        if (!user) return;
      
        // Fetch all teachers from the 'teachers' collection
         const teacherSnapshot = await getDocs(collection(db, 'teachers'));
      
        // Check if any document in the collection has the matching UID
        const isTeacher = teacherSnapshot.docs.some((doc) => doc.data().uid === user.uid);
      
        if (!isTeacher) {
           console.warn('User is not a Teacher. Signing out...');
      
           // Sign out the user
          await signOut(auth);
        }
      } catch (err) {
        console.error('Error verifying Teacher access:', err);
      
         // Sign out in case of an error
        await signOut(auth);
       }
    };
      
     verifyTeacher();
   }, [auth]); 
      

  // Fetch posts
  const fetchInitialPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const groupRef = doc(db, 'groups', groupId);
      const forumQuery = query(
        collection(groupRef, 'forum'),
        orderBy('createdAt', 'desc'),
        limit(6) // Fetch the first 6 posts
      );
      const forumSnapshot = await getDocs(forumQuery);
  
      const fetchedPosts = forumSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      const postsWithComments = await attachCommentsPreview(fetchedPosts);
      setPosts(postsWithComments);
      setLastVisible(forumSnapshot.docs[forumSnapshot.docs.length - 1]); // Track the last visible document
      setHasMore(forumSnapshot.docs.length === 6); // Determine if there are more posts
    } catch (err) {
      console.error('Error fetching initial posts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [groupId, attachCommentsPreview]);
  
  const fetchNextPosts = useCallback(async () => {
    if (!lastVisible || !hasMore || isLoading) return;
  
    setIsLoading(true);
    try {
      const groupRef = doc(db, 'groups', groupId);
      const forumQuery = query(
        collection(groupRef, 'forum'),
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(6)
      );
      const forumSnapshot = await getDocs(forumQuery);
  
      const fetchedPosts = forumSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      const postsWithComments = await attachCommentsPreview(fetchedPosts);
      setPosts((prevPosts) => [...prevPosts, ...postsWithComments]);
      setLastVisible(forumSnapshot.docs[forumSnapshot.docs.length - 1]);
      setHasMore(forumSnapshot.docs.length === 6);
    } catch (err) {
      console.error('Error fetching next posts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [lastVisible, hasMore, isLoading, groupId, attachCommentsPreview]);
  
  
  

  // Fetch comments for a selected post
  const fetchComments = useCallback(async (postId) => {
    try {
      const postRef = doc(db, 'groups', groupId, 'forum', postId);
      const commentsSnapshot = await getDocs(collection(postRef, 'comments'));
      const toMillis = (ts) =>
        ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : 0;
      const fetchedComments = commentsSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      setComments(fetchedComments);
      setSelectedPost((prev) =>
        prev && prev.id === postId ? { ...prev, comments: fetchedComments } : prev
      );
      return fetchedComments;
    } catch (err) {
      console.error('Erreur lors de la récupération des commentaires :', err);
      return [];
    }
  }, [groupId]); // Ajoute groupId comme dépendance
  
  const openComments = useCallback(
    async (post) => {
      if (!post) return;
      // Show existing preview immediately
      setComments(post.comments || []);
      setSelectedPost({ ...post, comments: post.comments || [] });
      setIsCommentsOverlayOpen(true);

      // Then fetch full list
      const fetched = await fetchComments(post.id);
      if (fetched.length) {
        setComments(fetched);
        setSelectedPost((prev) =>
          prev && prev.id === post.id ? { ...prev, comments: fetched } : prev
        );
      }
    },
    [fetchComments]
  );

  // Ensure comments are fetched when sheet opens (edge cases)
  useEffect(() => {
    if (isCommentsOverlayOpen && selectedPost?.id) {
      fetchComments(selectedPost.id).then((fetched) => {
        setComments(fetched);
        setSelectedPost((prev) =>
          prev && prev.id === selectedPost.id ? { ...prev, comments: fetched } : prev
        );
      });
    }
  }, [isCommentsOverlayOpen, selectedPost, fetchComments]);
  
  


  const uploadFiles = async (files) => {
    const urls = [];
    setIsUploading(true); // Activer l'état d'upload
    try {
      for (const file of files) {
        const uniqueName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `groups/${groupId}/forum/${uniqueName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
  
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress((prevProgress) => ({
                ...prevProgress,
                [file.name]: progress,
              }));
            },
            (error) => {
              console.error('Erreur lors de l’upload du fichier :', error);
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              urls.push(downloadURL);
              resolve();
            }
          );
        });
      }
    } catch (error) {
      console.error('Erreur lors de l’upload :', error);
    } finally {
      setIsUploading(false); // Désactiver l'état d'upload après l'upload
    }
    return urls;
  };
  
  // Create a post
  const createPost = async () => {
    if (!newTitle || !newContent) {
      alert(t('Le titre et le contenu sont requis.'));
      return;
    }
  
    try {
      const userUid = auth.currentUser.uid;
      let fullName = t('Utilisateur inconnu');
      let role = '';
  
      // Recherche dans teachers
      const teachersQuery = query(collection(db, 'teachers'), where('uid', '==', userUid));
      const teachersSnapshot = await getDocs(teachersQuery);
  
      if (!teachersSnapshot.empty) {
        const teacherDoc = teachersSnapshot.docs[0];
        const { firstName, lastName } = teacherDoc.data();
        fullName = `${firstName} ${lastName}`;
        role = 'teacher';
      } else {
        // Si non trouvé dans teachers, recherche dans students
        const studentsQuery = query(collection(db, 'students'), where('uid', '==', userUid));
        const studentsSnapshot = await getDocs(studentsQuery);
  
        if (!studentsSnapshot.empty) {
          const studentDoc = studentsSnapshot.docs[0];
          const { firstName, lastName } = studentDoc.data();
          fullName = `${firstName} ${lastName}`;
          role = 'student';
        }
      }
  
      // Upload des images
      const imageUrls = await uploadFiles(newImages);
  
      // Ajouter le post
      const groupRef = doc(db, 'groups', groupId);
      await addDoc(collection(groupRef, 'forum'), {
        title: newTitle,
        content: newContent,
        images: imageUrls,
        createdBy: userUid,
        full_name: fullName,
        role: role,
        createdAt: Timestamp.now(),
      });
  
      setPreviewImages([])
      setNewTitle('');
      setNewContent('');
      setNewImages([]);
      setUploadProgress({});
      setUploadProgress({}); // Réinitialiser la progression d'upload
      setIsDialogOpen(false);
      setSnackbarMessage(t('Publication créée avec succès !'));
      setIsSnackbarOpen(true);

      // Refresh the feed so the new post appears immediately
      await fetchInitialPosts();
    } catch (err) {
      console.error('Erreur lors de la création de la publication :', err);
    }
  };
  
  const updatePostComments = (postId, updatedComments) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.id === postId
          ? {
              ...post,
              comments: updatedComments.slice(0, 3), 
              commentsCount: updatedComments.length, 
            }
          : post
      )
    );
  };



const createComment = async () => {
  if (!newCommentContent.trim() && newCommentImages.length === 0) {
    alert(t('Vous devez saisir un texte ou ajouter un média (image/audio) avant de poster le commentaire.'));
    return;
  }

  try {
    const userUid = auth.currentUser.uid;
    let fullName = t('Utilisateur inconnu');
    let role = '';

    // Fetch user's full name and role
    const teachersQuery = query(collection(db, 'teachers'), where('uid', '==', userUid));
    const teachersSnapshot = await getDocs(teachersQuery);

    if (!teachersSnapshot.empty) {
      const teacherDoc = teachersSnapshot.docs[0];
      const { firstName, lastName } = teacherDoc.data();
      fullName = `${firstName} ${lastName}`;
      role = 'teacher';
    } else {
      const studentsQuery = query(collection(db, 'students'), where('uid', '==', userUid));
      const studentsSnapshot = await getDocs(studentsQuery);

      if (!studentsSnapshot.empty) {
        const studentDoc = studentsSnapshot.docs[0];
        const { firstName, lastName } = studentDoc.data();
        fullName = `${firstName} ${lastName}`;
        role = 'student';
      }
    }

    // Separate files and URLs from newCommentImages
    const filesOnly = newCommentImages.filter((item) => item instanceof File);
    const urlsOnly = newCommentImages.filter((item) => typeof item === 'string');

    // Upload files to Firebase Storage
    const uploadedFileUrls = await uploadFiles(filesOnly);

    // Combine uploaded URLs with pre-existing URLs
    const imageUrls = [...uploadedFileUrls, ...urlsOnly];

    // Add the new comment to Firestore
    const postRef = doc(db, 'groups', groupId, 'forum', selectedPost.id);
    const newComment = {
      content: newCommentContent,
      images: imageUrls,
      createdBy: userUid,
      full_name: fullName,
      role: role,
      createdAt: Timestamp.now(),
    };

    const addedCommentRef = await addDoc(collection(postRef, 'comments'), newComment);

    // Re-fetch updated comments from Firestore
    const commentsSnapshot = await getDocs(collection(postRef, 'comments'));
    const toMillis = (ts) =>
      ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : 0;
    const updatedComments = commentsSnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

    // Update comments state
    setComments(updatedComments);
    updatePostComments(selectedPost.id, updatedComments);
    setSelectedPost((prev) =>
      prev ? { ...prev, comments: updatedComments } : prev
    );

    // Reset input fields
    setNewCommentContent('');
    setNewCommentImages([]);
    setUploadProgress({});
    setSnackbarMessage(t('Commentaire ajouté avec succès !'));
    setIsSnackbarOpen(true);

    // Call the function to delete the notification after comment
    await deleteNotificationAfterComment(selectedPost.id);

  } catch (err) {
    console.error('Erreur lors de la création du commentaire :', err);
  }
};


const deleteNotificationAfterComment = async (postId) => {
  try {
    const userUid = auth.currentUser.uid;

    // Access the teacher's document
    const teacherQuery = query(collection(db, 'teachers'), where('uid', '==', userUid));
    const teacherSnapshot = await getDocs(teacherQuery);

    if (!teacherSnapshot.empty) {
      const teacherDoc = teacherSnapshot.docs[0]; // Assuming only one teacher document matches
      const teacherRef = doc(db, 'teachers', teacherDoc.id);

      // Access the notifications sub-collection
      const notificationsRef = collection(teacherRef, 'notifications');
      const notificationQuery = query(notificationsRef, where('postId', '==', postId));
      const notificationSnapshot = await getDocs(notificationQuery);

      // Delete the matching notification
      if (!notificationSnapshot.empty) {
        const batch = writeBatch(db); 
        notificationSnapshot.forEach((notificationDoc) => {
          const notificationRef = doc(notificationsRef, notificationDoc.id);
          batch.delete(notificationRef);
        });

        await batch.commit(); 
        console.log('Notification deleted successfully.');
      } else {
        console.log('No matching notification found.');
      }
    } else {
      console.log('Teacher not found.');
    }
  } catch (err) {
    console.error('Error deleting notification after comment:', err);
  }
};


  // Fonction utilitaire pour supprimer un fichier de Firebase Storage
const deleteFileFromStorage = async (fileUrl) => {
  try {
    const fileRef = ref(storage, fileUrl);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Erreur lors de la suppression du fichier :', error);
  }
};

const deletePost = async (postId) => {
  if (!window.confirm(t('Êtes-vous sûr de vouloir supprimer cette publication ?'))) {
    return;
  }

  try {
    const groupRef = doc(db, 'groups', groupId);
    const postRef = doc(collection(groupRef, 'forum'), postId);

    // Récupérer le post pour obtenir les URLs d'images
    const postSnapshot = await getDoc(postRef);
    const postData = postSnapshot.data();

    // Supprimer les fichiers images du post
    if (postData.images && postData.images.length > 0) {
      await Promise.all(postData.images.map(deleteFileFromStorage));
    }

    // Supprimer les commentaires et leurs images
    const commentsRef = collection(postRef, 'comments');
    const commentsSnapshot = await getDocs(commentsRef);

    for (const commentDoc of commentsSnapshot.docs) {
      const commentData = commentDoc.data();
      
      // Supprimer les images du commentaire
      if (commentData.images && commentData.images.length > 0) {
        await Promise.all(commentData.images.map(deleteFileFromStorage));
      }

      // Supprimer le commentaire
      await deleteDoc(commentDoc.ref);
    }

    // Supprimer le document du post
    await deleteDoc(postRef);

    setPosts((prev) => prev.filter((p) => p.id !== postId));
    if (selectedPost?.id === postId) {
      setSelectedPost(null);
      setIsCommentsOverlayOpen(false);
    }

    setSnackbarMessage(t('Publication supprimée avec succès !'));
    setIsSnackbarOpen(true);
  } catch (err) {
    console.error('Erreur lors de la suppression de la publication :', err);
  }
};

const deleteComment = async (commentId) => {
  const comment = comments.find((c) => c.id === commentId);
  if (!comment) {
    alert(t('Commentaire introuvable.'));
    return;
  }

  try {
    const postRef = doc(db, 'groups', groupId, 'forum', selectedPost.id);

    // Supprimer les fichiers images du commentaire
    if (comment.images && comment.images.length > 0) {
      await Promise.all(comment.images.map(deleteFileFromStorage));
    }

    // Supprimer le commentaire dans Firestore
    await deleteDoc(doc(collection(postRef, 'comments'), commentId));

    // Mettre à jour localement
    const updatedComments = comments.filter((c) => c.id !== commentId);
    setComments(updatedComments); // Met à jour les commentaires dans le Drawer
    updatePostComments(selectedPost.id, updatedComments); // Met à jour dans la liste principale

    setSnackbarMessage(t('Commentaire supprimé avec succès !'));
    setIsSnackbarOpen(true);
  } catch (err) {
    console.error('Erreur lors de la suppression du commentaire :', err);
  }
};

  // Initialize data
  useEffect(() => {
    fetchInitialPosts();
  }, [fetchInitialPosts]);
  

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading) {
          fetchNextPosts();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPosts, hasMore, isLoading]);
  
  // 🔹 Apply filter + sorting for:
  const getVisiblePosts = () => {
    let base = [...posts];

    if (postFilter === 'today') {
      base = base.filter((p) => isToday(p.createdAt));
      base.sort((a, b) => tsToMillis(b.createdAt) - tsToMillis(a.createdAt));
      return base;
    }

    if (postFilter === 'teachers') {
      base = base.filter((p) => p.role === 'teacher');
      base.sort((a, b) => tsToMillis(b.createdAt) - tsToMillis(a.createdAt));
      return base;
    }

    if (postFilter === 'mostEngaging') {
      base.sort(
        (a, b) => (b.commentsCount || 0) - (a.commentsCount || 0)
      );
      return base;
    }

    // 'all'
    base.sort((a, b) => tsToMillis(b.createdAt) - tsToMillis(a.createdAt));
    return base;
  };

  const visiblePosts = getVisiblePosts();
  
  const showComingSoon = () => {
    setSnackbarMessage(t('سيتم إضافة هذه الميزة قريبًا!'));
    setIsSnackbarOpen(true);
  };


  return (
    <div
      style={{
        background:
          'radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Space Grotesk", "Work Sans", system-ui, sans-serif',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <TeacherNavbar />
      <Box
        sx={{
          px: 2,
          py: 2,
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
      <Box
        sx={{
          px: 2,
          py: 3,
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Top bar: back + new post */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' }, // 👉 show ONLY on phone
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              const currentPath = window.location.pathname;
              const newPath = currentPath.replace('/forum', '');
              window.location.href = newPath;
            }}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: '30px',
              textTransform: 'none',
              color: '#12344d',
              borderColor: '#d0d5dd',
            }}
          >
            {t('الملفات')}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsDialogOpen(true)}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: '30px',
              textTransform: 'none',
              fontWeight: 'bold',
              px: 2.5,
            }}
          >
            {t('إضافة منشور')}
          </Button>
        </Box>

        {/* Main two-column layout */}
        <Box
          sx={{
            px: 2,
            py: 1,
            maxWidth: '1200px',
            mx: 'auto',
            width: '100%',
            boxSizing: 'border-box',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.6fr) 260px' },
            gap: 3,
            alignItems: 'flex-start',
            maxHeight: { md: 'calc(100vh - 80px)' }, // 80px ≈ navbar height
            overflow: { md: 'hidden' },
          }}
        >
          {/* LEFT COLUMN – posts list */}
          <Box
            sx={{
            overflowY: { md: 'auto' },
            maxHeight: { md: 'calc(100vh - 80px)' }, // same value as above
            pr: { md: 1 },
            minWidth: 0,
          }}>
            {visiblePosts.length === 0 && !isLoading && (
              <Box
                sx={{
                  minHeight: { xs: 220, md: 360 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 2,
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                    {t('لا توجد منشورات بعد')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#64748b' }}>
                    {t('ابدأ بإضافة أول منشور لبدء النقاش داخل المنتدى.')}
                  </Typography>
                </Box>
              </Box>
            )}

            {visiblePosts.map((post) => (
              <div
                key={post.id}
                id={`post-${post.id}`}
                style={{
                  backgroundColor: '#fff',
                  padding: '16px',
                  marginBottom: '18px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                  border: '1px solid #eef0f4',
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Avatar variant="square" sx={{ bgcolor: '#12344d', width: 40, height: 40, fontWeight: 'bold' }}>
                      {getInitials(post.full_name)}
                    </Avatar>
                    <div>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 'bold',
                          color: '#1C1E21',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {post.full_name}
                        {post.role === 'teacher' && (
                          <VerifiedIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#65676B' }}>
                        {formatDate(post.createdAt)}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Chip
                      label={post.role === 'teacher' ? t('أستاذ') : t('طالب')}
                      size="small"
                      sx={{
                        backgroundColor: '#f0f4ff',
                        color: '#1d4ed8',
                        fontWeight: 600,
                      }}
                    />
                    {hasDeletePostPermission(post) && (
                      <IconButton onClick={() => deletePost(post.id)} sx={{ color: '#F3425F' }}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </div>
                </div>

                <Divider/>

                {/* Title & content */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0f172a', mt: 2, mb: 1 }}>
                  {post.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#1C1E21', lineHeight: 1.6, mb: 1.5 }}>
                  {post.content}
                </Typography>


                {/* Media carousel */}
                {post.images && post.images.length > 0 && (
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        pb: '6px',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                      }}
                      onScroll={(e) => {
                        const width = e.currentTarget.clientWidth;
                        const idx = Math.round(e.currentTarget.scrollLeft / width);
                        setCarouselIndex((prev) => ({ ...prev, [post.id]: idx }));
                      }}
                    >
                      <PhotoProvider>
                        {post.images.map((image, index) => (
                          <PhotoView key={index} src={image}>
                            <img
                              src={image}
                              alt={t('Publication')}
                              style={{
                                minWidth: '100%',
                                maxWidth: '100%',
                                height: '520px',
                                objectFit: 'cover',
                                borderRadius: '14px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                scrollSnapAlign: 'center',
                              }}
                            />
                          </PhotoView>
                        ))}
                      </PhotoProvider>
                    </Box>

                    {post.images.length > 1 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 6,
                          marginTop: 8,
                        }}
                      >
                        {post.images.map((_, idx) => (
                          <span
                            key={idx}
                            style={{
                              width: idx === (carouselIndex[post.id] ?? 0) ? 10 : 8,
                              height: idx === (carouselIndex[post.id] ?? 0) ? 10 : 8,
                              borderRadius: '50%',
                              backgroundColor:
                                idx === (carouselIndex[post.id] ?? 0)
                                  ? '#1877F2'
                                  : '#d0d5dd',
                              transition: 'all 0.2s ease',
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                    <Button
                      variant="text"
                      onClick={() => openComments(post)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: '#1877F2',
                      px: 0,
                    }}
                    startIcon={
                      <ChatBubbleOutlineIcon sx={{ marginRight: '7px', color: '#65676B' }} />
                    }
                    >
                      {t('{{count}} تعليقات', { count: post.comments?.length || 0 })}
                    </Button>
                </div>

                <Divider sx={{ backgroundColor: '#E4E6EB', marginY: '10px' }} />

                {/* Comments preview */}
                <div style={{ marginTop: '8px', marginBottom: '6px' }}>
                  {post.comments?.slice(0, 3).map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        padding: '10px',
                        marginBottom: '6px',
                        backgroundColor: '#F7F8FB',
                        borderRadius: '10px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar
                          sx={{
                            bgcolor: '#e0e7ff',
                            width: 32,
                            height: 32,
                            fontSize: 14,
                            color: '#1d4ed8',
                            fontWeight: 'bold',
                          }}
                        >
                          {getInitials(comment.full_name || t('؟'))}
                        </Avatar>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'bold',
                            color: '#1C1E21',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {comment.full_name || t('Utilisateur inconnu')}
                          {comment.role === 'teacher' && (
                            <VerifiedIcon sx={{ color: '#1877F2', fontSize: '14px' }} />
                          )}
                        </Typography>
                      </div>

                      <Typography
                        variant="body2"
                        sx={{ marginTop: '6px', color: '#1C1E21' }}
                      >
                        {comment.content}
                      </Typography>

                      {comment.images && comment.images.length > 0 && (
                        <>
                          <PhotoProvider>
                            <div
                              style={{
                                display: 'flex',
                                gap: '6px',
                                flexWrap: 'wrap',
                                marginTop: '6px',
                              }}
                            >
                              {comment.images
                                .filter((mediaUrl) => {
                                  const lower = mediaUrl.toLowerCase();
                                  return !lower.includes('.webm') && !lower.includes('.mp3');
                                })
                                .map((imageUrl, index) => (
                                  <PhotoView src={imageUrl} key={index}>
                                    <img
                                      src={imageUrl}
                                      alt={t('Média')}
                                      style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '8px',
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  </PhotoView>
                                ))}
                            </div>
                          </PhotoProvider>

                          <div
                            style={{
                              display: 'flex',
                              gap: '6px',
                              flexWrap: 'wrap',
                              marginTop: '6px',
                            }}
                          >
                            {comment.images
                              .filter((mediaUrl) => {
                                const lower = mediaUrl.toLowerCase();
                                return lower.includes('.webm') || lower.includes('.mp3');
                              })
                              .map((audioUrl, index) => (
                                <audio
                                  key={index}
                                  controls
                                  src={audioUrl}
                                  style={{ width: '120px' }}
                                  type="audio/webm"
                                >
                                  {t("Votre navigateur ne supporte pas l'élément audio.")}
                                </audio>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                  {isLoading && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <Typography variant="body2" sx={{ color: '#65676B' }}>
                      {t('Chargement...')}
                      </Typography>
                    </div>
                  )}

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Button
                    variant="text"
                    onClick={() => openComments(post)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: '#1877F2',
                      '&:hover': {
                        backgroundColor: 'rgba(24, 119, 242, 0.1)',
                      },
                    }}
                    >
                    <ChatBubbleOutlineIcon sx={{ marginRight: '7px', color: '#65676B' }} /> {t('أترك تعليق')}
                  </Button>
                </div>
              </div>
            ))}

            <div ref={loadMoreRef} />
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  disabled={isLoading}
                  onClick={fetchNextPosts}
                  sx={{ minWidth: '180px' }}
                >
                  {isLoading ? t('جار التحميل...') : t('تحميل المزيد')}
                </Button>
              </Box>
            )}
          </Box>

          {/* RIGHT COLUMN – sidebar */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'sticky',
              height: 'fit-content',
              width: '240px',
              justifySelf: 'end',
              alignSelf: 'start',
            }}
          >
            <Button
                variant="contained"
                fullWidth
                onClick={() => setIsDialogOpen(true)}
                sx={{
                  mb: 2,
                  borderRadius: '7px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  backgroundColor: '#1877F2',
                }}
              >
                {t('إضافة منشور')}
              </Button>

              <Button
              variant="outlined"
              fullWidth
              color="inherit"
              onClick={() => {
                const currentPath = window.location.pathname;
                const newPath = currentPath.replace('/forum', '');
                window.location.href = newPath;
              }}
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: '7px',
                textTransform: 'none',
                color: '#12344d',
                fontSize: '15px',
                borderColor: '#d0d5dd',
              }}
            >
              {t('العودة إلى الملفات')}
            </Button>

            {/* Filter posts (All / Today / Teachers) */}
            <Box sx={{ mb: 2, mt:3, direction: 'rtl' }}>
              <List dense sx={{ p: 0 }}>
                {/* All posts */}
                <ListItemButton
                  onClick={() => setPostFilter('all')}
                  selected={postFilter === 'all'}
                  sx={{
                    borderRadius: '999px',
                    mb: 0.5,
                    px: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(24,118,242,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {/* right side: icon + label (RTL) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChatBubbleOutlineIcon
                        sx={{ fontSize: 20, color: '#1877F2' }}
                      />
                        <Typography
                          sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#111827' }}
                        >
                        {t('كل المنشورات')}
                        </Typography>
                    </Box>

                    {/* left side: check */}
                    {postFilter === 'all' && (
                      <CheckIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                    )}
                  </Box>
                </ListItemButton>

                {/* Today's posts */}
                <ListItemButton
                  onClick={() => setPostFilter('today')}
                  selected={postFilter === 'today'}
                  sx={{
                    borderRadius: '999px',
                    mb: 0.5,
                    px: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(24,118,242,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TodayOutlinedIcon
                        sx={{ fontSize: 20, color: '#1877F2' }}
                      />
                        <Typography
                          sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#111827' }}
                        >
                        {t('منشورات اليوم')}
                        </Typography>
                    </Box>

                    {postFilter === 'today' && (
                      <CheckIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                    )}
                  </Box>
                </ListItemButton>


                {/* Most Engaged posts */}
                <ListItemButton
                  onClick={() => setPostFilter('mostEngaging')}
                  selected={postFilter === 'mostEngaging'}
                  sx={{
                    borderRadius: '999px',
                    mb: 0.5,
                    px: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(24,118,242,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatshotIcon
                        sx={{ fontSize: 20, color: '#1877F2' }}
                      />
                        <Typography
                          sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#111827' }}
                        >
                        {t('المنشورات الأكثر تفاعلا')}
                        </Typography>
                    </Box>

                    {postFilter === 'mostEngaging' && (
                      <CheckIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                    )}
                  </Box>
                </ListItemButton>

                {/* Teacher posts only */}
                <ListItemButton
                  onClick={() => setPostFilter('teachers')}
                  selected={postFilter === 'teachers'}
                  sx={{
                    borderRadius: '999px',
                    mb: 0.5,
                    px: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(24,118,242,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolOutlinedIcon
                        sx={{ fontSize: 20, color: '#1877F2' }}
                      />
                        <Typography
                          sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#111827' }}
                        >
                        {t('منشورات الأستاذ فقط')}
                        </Typography>
                    </Box>

                    {postFilter === 'teachers' && (
                      <CheckIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                    )}
                  </Box>
                </ListItemButton>
              </List>
            </Box>

            <Divider/>

            {/* Help / policy section – place this INSIDE your right sidebar <Box> under "الأقسام" */}
            <Box
              sx={{
                mt: 1,
              }}
              dir="ltr"
            >

              <Stack spacing={1.2}>
                {/* FAQ */}
                <Box
                  onClick={showComingSoon}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#EEF2FF' },
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827' }}>
                    {t('الأسئلة الشائعة (FAQ)')}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '999px',
                      bgcolor: '#FEF3C7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HelpOutlineOutlinedIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                  </Box>
                </Box>

                {/* How to use the forum */}
                <Box
                  onClick={showComingSoon}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#ECFEFF' },
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827' }}>
                    {t('كيف تستخدم المنتدى؟')}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '999px',
                      bgcolor: '#E0F2FE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#0284C7' }} />
                  </Box>
                </Box>

                {/* Posting policy */}
                <Box
                  onClick={showComingSoon}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#FEF2F2' },
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827' }}>
                    {t('سياسة النشر')}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '999px',
                      bgcolor: '#FEE2E2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <GavelOutlinedIcon sx={{ fontSize: 18, color: '#B91C1C' }} />
                  </Box>
                </Box>

                {/* Report a problem */}
                <Box
                  onClick={showComingSoon}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#FFF7ED' },
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827' }}>
                    {t('الإبلاغ عن مشكلة')}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '999px',
                      bgcolor: '#FFEDD5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ReportGmailerrorredOutlinedIcon sx={{ fontSize: 18, color: '#EA580C' }} />
                  </Box>
                </Box>

                {/* Contact support */}
                <Box
                  onClick={showComingSoon}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#ECFDF5' },
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827' }}>
                    {t('التواصل مع الدعم الفني')}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '999px',
                      bgcolor: '#DCFCE7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SupportAgentOutlinedIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom sheet pour les commentaires */}
      <MuiDialog
        fullWidth
        maxWidth="md"
        open={isCommentsOverlayOpen}
        onClose={closeCommentsOverlay}
        TransitionComponent={BottomTransition}
        PaperProps={{
          sx: {
            m: 0,
            borderRadius: { xs: '16px 16px 0 0', sm: '16px' },
            pb: 0,
            background: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: '90vh', sm: '90vh' },
          },
        }}
      >
          <Box sx={{ p: 2, borderBottom: '1px solid #eef0f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1C1E21' }}>
              {selectedPost?.title || t('Chargement...')}
            </Typography>
            <IconButton onClick={closeCommentsOverlay}>
              <CloseIcon sx={{ color: '#65676B' }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 1, pb: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ marginBottom: '16px', color: '#1C1E21' }}>
                {selectedPost?.content || ''}
              </Typography>

              {selectedPost?.images?.length > 0 && (
                <PhotoProvider>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', marginBottom: '8px' }}>
                    {selectedPost.images.map((image, index) => (
                      <PhotoView key={index} src={image}>
                        <img
                          src={image}
                          alt={t('Im {{count}}', { count: index + 1 })}
                          style={{
                            width: '100%',
                            maxWidth: '500px',
                            height: 'auto',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                            cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                          }}
                        />
                      </PhotoView>
                    ))}
                  </div>
                </PhotoProvider>
              )}

              <Divider sx={{ marginTop: '8px', backgroundColor: '#E4E6EB' }} />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                marginBottom: '10px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#1C1E21' 
              }}>
                {t('التعليقات')}
              </Typography>

              {comments.length > 0 ? (
                  comments.map((comment) => (
                  <div
                    key={comment.id}
                    style={{
                      padding: '10px',
                      marginBottom: '10px',
                      borderRadius: '10px',
                      backgroundColor: '#F7F8FB',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: '#1C1E21' }}>
                        {comment.full_name || t('Utilisateur inconnu')}
                        {comment.role === 'teacher' && (
                          <VerifiedIcon sx={{ color: '#1877F2', fontSize: '14px', marginLeft: '8px' }} />
                        )}
                      </Typography>

                      {hasDeleteCommentPermission(comment) && (
                        <IconButton onClick={() => deleteComment(comment.id)} sx={{ color: '#F3425F' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                    <Typography variant="body2" sx={{ color: '#1C1E21', mt: 0.5 }}>{comment.content}</Typography>

                    {/* Images et audios des commentaires */}
                    {comment.images && comment.images.length > 0 && (
                      <>
                        {/* Images */}
                        <PhotoProvider>
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '5px' }}>
                            {comment.images
                              .filter(mediaUrl => {
                                const lower = mediaUrl.toLowerCase();
                                return !lower.includes('.webm') && !lower.includes('.mp3');
                              })
                              .map((imageUrl, index) => (
                                <PhotoView src={imageUrl} key={index}>
                                  <img
                                    src={imageUrl}
                                    alt={t('Média')}
                                    style={{
                                      width: '100px',
                                      height: '100px',
                                      borderRadius: '8px',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                    }}
                                  />
                                </PhotoView>
                              ))}
                          </div>
                        </PhotoProvider>

                        {/* Audios */}
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '5px' }}>
                          {comment.images
                            .filter(mediaUrl => {
                              const lower = mediaUrl.toLowerCase();
                              return lower.includes('.webm') || lower.includes('.mp3');
                            })
                            .map((audioUrl, index) => (
                              <audio key={index} controls src={audioUrl} style={{ width: '100px' }} type="audio/webm">
                                {t("Votre navigateur ne supporte pas l'élément audio.")}
                              </audio>
                            ))}
                        </div>
                      </>
                    )}

                    <Typography variant="caption" sx={{ 
                      marginTop: '8px', 
                      display: 'block',
                      color: '#65676B'
                    }}>
                      {new Date(comment.createdAt?.toDate()).toLocaleString()}
                    </Typography>
                    </div>
                      ))
                    ) : (
                        <Typography variant="body2" 
                          sx={{
                            textAlign: 'center',
                            marginTop: '20px',
                            color: '#65676B', 
                            fontStyle: 'italic',
                            marginBottom: '20px'
                          }}>
                          {t('لا توجد تعليقات في الوقت الحالي')}
                        </Typography>
                    )}
            </Box>
          </Box>

          {/* Champ d'ajout de commentaire */}
          <Box 
            sx={{ 
              position: 'sticky', 
              bottom: 0,
              background: '#FFFFFF',
              p: 2,
              borderTop: '1px solid #eef0f4',
            }}>
            {hasCommentPermission(selectedPost) && (
              <>
                <Paper
                  component="form"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    boxShadow: 'none',
                    border: '1px solid #E4E6EB',
                    borderRadius: '20px',
                    backgroundColor: '#F0F2F5',
                    padding: '4px 6px',
                    marginBottom: '12px'
                  }}
                >
                  <InputBase
                    multiline
                    placeholder={t('أترك تعليق')}
                    rows={1}
                    fullWidth
                    sx={{ 
                      padding: {
                       xs: '6px', 
                       sm: '10px', 
                     },
                     flexGrow: 1,
                      color: '#1C1E21',
                     fontSize: {
                       xs: '0.875rem', 
                        sm: '1rem',
                      },
                     }}
                    value={newCommentContent}
                    onChange={(e) => setNewCommentContent(e.target.value)}
                  />

                  <label htmlFor="comment-image-upload">
                    <input
                      id="comment-image-upload"
                      type="file"
                      accept="image/*,audio/*"
                      multiple
                      onChange={(e) =>
                        setNewCommentImages((prevImages) => [...prevImages, ...Array.from(e.target.files)])
                      }
                      style={{ display: 'none' }}
                    />
                    <IconButton component="span" sx={{ color: '#1877F2', padding: { xs: '6px', sm: '10px' } }}
                       disabled={isUploading}>
                      <InsertPhotoIcon />
                    </IconButton>
                  </label>

                  <IconButton
                    sx={{ color: '#1877F2', padding: { xs: '6px', sm: '10px' } }}

                    onClick={() => setShowVoiceRecorder(prev => !prev)}
                  >
                    <MicIcon />
                  </IconButton>

                  <IconButton
                    color="primary"
                    onClick={createComment}
                    disabled={isUploading || (!newCommentContent.trim() && newCommentImages.length === 0)}
                    sx={{ 
                      color: newCommentContent.trim() || newCommentImages.length > 0 
                        ? '#1877F2' 
                        : '#BEC3C9',
                      padding: { xs: '6px', sm: '10px' },
                    }}
                    
                  >
                      <SendIcon />
                  </IconButton>
                </Paper>

                {/* Preview des médias ajoutés */}
                {newCommentImages.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px'}}>
                    {newCommentImages.map((item, index) => {
                      const isFile = item instanceof File;
                      const previewUrl = isFile ? URL.createObjectURL(item) : item;

                      let isImage = false;
                      let isAudio = false;

                      if (isFile) {
                        if (item.type.startsWith('image/')) {
                          isImage = true;
                        } else if (item.type.startsWith('audio/')) {
                          isAudio = true;
                        }
                      } else {
                        const lowerUrl = previewUrl.toLowerCase();
                        if (
                          lowerUrl.endsWith('.png') ||
                          lowerUrl.endsWith('.jpg') ||
                          lowerUrl.endsWith('.jpeg') ||
                          lowerUrl.endsWith('.gif')
                        ) {
                          isImage = true;
                        } else if (
                          lowerUrl.endsWith('.webm') ||
                          lowerUrl.endsWith('.mp3')
                        ) {
                          isAudio = true;
                        }
                      }

                      return (
                        <div key={index} style={{ position: 'relative' }}>
                          {isImage && (
                            <img
                              src={previewUrl}
                              alt={t('Preview')}
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '5px',
                                objectFit: 'cover',
                                border: '1px solid #E4E6EB',
                              }}
                            />
                          )}

                          {isAudio && (
                            <audio
                              controls
                              src={previewUrl}
                              style={{ width: '100px' }}
                            >
                              {t("Votre navigateur ne supporte pas l'élément audio.")}
                            </audio>
                          )}

                          {!isImage && !isAudio && (
                            <p style={{ fontStyle: 'italic', margin: 0 }}>
                              {t('Fichier non pris en charge')}
                            </p>
                          )}

                          <IconButton
                            size="small"
                            onClick={() =>
                              setNewCommentImages((prevImages) => prevImages.filter((_, i) => i !== index))
                            }
                            sx={{
                              position: 'absolute',
                              top: '-5px',
                              right: '-5px',
                              backgroundColor: '#FFFFFF',
                              color: '#F3425F',
                              border: '1px solid #E4E6EB',
                              width: '20px',
                              height: '20px',
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      );
                    })}
                  </div>
                )}
          
                {showVoiceRecorder && (
                    <VoiceCommentRecorder
                      onAudioRecorded={handleAudioRecorded}
                    />
                )}
              </>
            )}
          </Box>
        </MuiDialog>
      </Box>

      {/* Dialog pour créer un post */}
      <Dialog open={isDialogOpen} onClose={() => { setIsDialogOpen(false); resetCreatePostFields(); }} sx={{
        '& .MuiDialog-paper': {
          width: { xs: '100%', sm: '72%', md: '48%' },
          maxWidth: 'none',
          borderRadius: 3,
          transition: 'width 0.3s ease',
          boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
        },
      }}>
        <DialogTitle sx={{ fontWeight: '800', textAlign: 'center', fontSize: '24px', color: '#0f172a', letterSpacing: '0.3px' }}>
          {t('إضافة منشور')}
        </DialogTitle>

        <DialogContent sx={{ padding: '22px', background: '#f8fafc' }}>
          {/* Zone de téléchargement des fichiers */}
          <div
            style={{
              border: '2px dashed #d0d5dd',
              height: '220px',
              borderRadius: '14px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: '16px',
              background: '#fff',
              overflow: 'hidden',
            }}
            onClick={() => document.getElementById('file-upload').click()}
          >
            {previewImages.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  overflowX: 'auto',
                  scrollSnapType: 'x mandatory',
                  width: '100%',
                  height: '100%',
                }}
              >
                {previewImages.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={t('Preview {{count}}', { count: idx + 1 })}
                    style={{
                      minWidth: '100%',
                      maxWidth: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      scrollSnapAlign: 'center',
                    }}
                  />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#475467', fontWeight: 'bold' }}>
                  {t('أضف صورًا للمنشور')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#98a2b3', mt: 1 }}>
                  {t('اسحب وأفلت أو انقر للتحميل')}
                </Typography>
              </div>
            )}
          </div>
          
          <input
            id="file-upload"
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleAddImages(e.target.files)}
            style={{ display: 'none' }}
          />

          {/* Champ du titre */}
          <div style={{ marginBottom: '10px' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '6px', color: '#0f172a' }}>
              {t('الموضوع')}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={t('أدخل عنوان الموضوع هنا...')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              sx={{
                background: '#fff',
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              }}
            />
          </div>

          {/* Champ de description */}
          <div style={{ marginBottom: '15px' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '6px', color: '#0f172a' }}>
              {t('تفاصيل المنشور')}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={t('اكتب التفاصيل هنا...')}
              value={newContent}
              multiline
              rows={4}
              onChange={(e) => setNewContent(e.target.value)}
              sx={{
                background: '#fff',
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              }}
            />
          </div>

          {/* Progression de l'upload */}
          {Object.keys(uploadProgress).map((fileName) => (
            <div key={fileName} style={{ marginTop: '10px' }}>
              <Typography variant="body2" sx={{ marginBottom: '5px' }}>
                {fileName}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress[fileName]}
                sx={{
                  height: '8px',
                  borderRadius: '5px',
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                  backgroundColor: '#007bff',
                },
              }}
            />
          </div>
          ))}

          {/* Preview des images sélectionnées */}
          {previewImages.length > 0 && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px', marginBottom: '10px'}}>
                {previewImages.map((src, index) => (
                <div key={index} style={{ position: 'relative' }}>
                  <img
                    src={src}
                    alt={t('Preview')}
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                      border: '1px solid #E4E6EB',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePreview(index)}
                    sx={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      backgroundColor: '#FFFFFF',
                      color: '#F3425F',
                      border: '1px solid #E4E6EB',
                      width: '20px',
                      height: '20px',
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={createPost}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isUploading}
            sx={{
              padding: '10px 20px',
              fontWeight: 'bold',
              backgroundColor: '#4A90E2',
              '&:hover': { backgroundColor: '#3b7dc3' },
            }}
          >
            {t('نشر')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            aria-label={t('fermer')}
            color="inherit"
            onClick={() => setIsSnackbarOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
    
}

export default TeacherForum;
